import { redactPrivateBearerPath } from '@/lib/privacy/private-routes';
import { redactCredentials } from '@/lib/observability/log-sanitize';

const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PII_KEY_PATTERN =
  /(^|_)(email|email_address|full_name|first_name|last_name|display_name|user_name|username|name|phone|phone_number|address)(_|$)/i;
const URL_KEY_PATTERN = /(^|_)(url|referrer)$/i;
const PATHNAME_KEY_PATTERN = /(^|_)pathname$/i;

type HeadersLike = Pick<Headers, 'get'>;

export type AnalyticsRequestContext = {
  currentUrl?: string;
  pathname?: string;
  host?: string;
  routeGroup?: 'dashboard' | 'onboarding' | 'public';
  referrer?: string;
  referringDomain?: string;
  ip?: string;
  userAgent?: string;
  country?: string;
  requestId?: string;
};

export function sanitizeAnalyticsPathname(value: string): string {
  return redactPrivateBearerPath(value);
}

export function sanitizeAnalyticsUrl(value: string): string | undefined {
  try {
    const url = new URL(value);
    return `${url.origin}${sanitizeAnalyticsPathname(url.pathname)}`;
  } catch {
    return undefined;
  }
}

/** Also covers exception messages and stacks, which can carry request URLs. */
export function sanitizeAnalyticsText(value: string): string {
  return redactCredentials(value.replace(EMAIL_PATTERN, '[redacted-email]'));
}

function normalizeAnalyticsKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

function sanitizeValue(key: string, value: unknown): unknown {
  const normalizedKey = normalizeAnalyticsKey(key);
  if (typeof value === 'string') {
    if (normalizedKey === 'pathname') {
      return sanitizeAnalyticsPathname(value);
    }
    if (URL_KEY_PATTERN.test(normalizedKey)) {
      return sanitizeAnalyticsUrl(value);
    }
    if (PATHNAME_KEY_PATTERN.test(normalizedKey)) {
      return sanitizeAnalyticsPathname(value);
    }
    return sanitizeAnalyticsText(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(key, item)).filter((item) => item !== undefined);
  }

  if (value && typeof value === 'object') {
    return sanitizeAnalyticsProperties(value as Record<string, unknown>);
  }

  return value;
}

/**
 * Last-line protection for analytics payloads. Call sites should still avoid
 * collecting personal data in the first place.
 */
export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || PII_KEY_PATTERN.test(normalizeAnalyticsKey(key))) continue;
    const safeValue = sanitizeValue(key, value);
    if (safeValue !== undefined) sanitized[key] = safeValue;
  }

  return sanitized;
}

function getRouteGroup(pathname: string): AnalyticsRequestContext['routeGroup'] {
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) return 'dashboard';
  if (pathname === '/onboarding' || pathname.startsWith('/onboarding/')) return 'onboarding';
  return 'public';
}

export function buildHeadersAnalyticsContext(
  requestHeaders: HeadersLike,
  currentUrl?: string,
): AnalyticsRequestContext {
  const sanitizedCurrentUrl = currentUrl ? sanitizeAnalyticsUrl(currentUrl) : undefined;
  const parsedCurrentUrl = sanitizedCurrentUrl ? new URL(sanitizedCurrentUrl) : undefined;
  const rawReferrer = requestHeaders.get('referer') ?? requestHeaders.get('referrer');
  const referrer = rawReferrer ? sanitizeAnalyticsUrl(rawReferrer) : undefined;
  const parsedReferrer = referrer ? new URL(referrer) : undefined;

  return {
    currentUrl: sanitizedCurrentUrl,
    pathname: parsedCurrentUrl?.pathname,
    host: parsedCurrentUrl?.host,
    routeGroup: parsedCurrentUrl ? getRouteGroup(parsedCurrentUrl.pathname) : undefined,
    referrer,
    referringDomain: parsedReferrer?.host,
    ip: requestHeaders.get('x-forwarded-for')?.split(',')[0]?.trim() || undefined,
    userAgent: requestHeaders.get('user-agent') || undefined,
    country:
      requestHeaders.get('x-vercel-ip-country') || requestHeaders.get('x-country') || undefined,
    requestId: requestHeaders.get('x-vercel-id') || requestHeaders.get('x-request-id') || undefined,
  };
}

export function buildRequestAnalyticsContext(request: Request): AnalyticsRequestContext {
  return buildHeadersAnalyticsContext(request.headers, request.url);
}

export function requestContextToPostHogProperties(
  context: AnalyticsRequestContext,
): Record<string, string> {
  const properties = {
    $current_url: context.currentUrl,
    $pathname: context.pathname,
    $host: context.host,
    $referrer: context.referrer,
    $referring_domain: context.referringDomain,
    $ip: context.ip,
    $raw_user_agent: context.userAgent,
    country: context.country,
    request_id: context.requestId,
    route_group: context.routeGroup,
  };

  return Object.fromEntries(
    Object.entries(properties).filter((entry): entry is [string, string] => Boolean(entry[1])),
  );
}
